import Papa from 'papaparse';

/**
 * Convert JSON data to CSV string
 * @param {Array} jsonData - Array of objects to convert to CSV
 * @returns {string} - CSV string
 */
export const convertToCSV = (jsonData) => {
  return Papa.unparse(jsonData);
};

/**
 * Parse CSV string to JSON data
 * @param {string} csvString - CSV string to parse
 * @returns {Array} - Array of objects parsed from CSV
 */
export const parseCSV = (csvString) => {
  const result = Papa.parse(csvString, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
    transform: (value) => value.trim()
  });
  
  if (result.errors && result.errors.length > 0) {
    console.error('CSV parsing errors:', result.errors);
  }
  
  // Convert empty strings to null
  const cleanedData = result.data.map(item => {
    const cleanedItem = {};
    Object.entries(item).forEach(([key, value]) => {
      cleanedItem[key] = value === '' ? null : value;
    });
    return cleanedItem;
  });
  
  return cleanedData;
};

/**
 * Get template headers for a collection type
 * @param {string} collectionType - Type of collection
 * @returns {Object} - Object with headers as keys and empty values
 */
export const getTemplateRow = (collectionType) => {
  switch (collectionType) {
    case 'games':
      return {
        _id: '',
        rawgId: '',
        name: '',
        platform: '',
        releaseYear: '',
        genre: '',
        developer: '',
        publisher: '',
        region: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'consoles':
      return {
        _id: '',
        name: '',
        manufacturer: '',
        releaseYear: '',
        model: '',
        color: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'legoSets':
      return {
        _id: '',
        name: '',
        setNumber: '',
        theme: '',
        releaseYear: '',
        pieces: '',
        minifigures: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'movies':
      return {
        _id: '',
        name: '',
        director: '',
        releaseYear: '',
        format: '',
        genre: '',
        studio: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'music':
      return {
        _id: '',
        name: '',
        artist: '',
        releaseYear: '',
        format: '',
        genre: '',
        label: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'books':
      return {
        _id: '',
        name: '',
        author: '',
        releaseYear: '',
        format: '',
        genre: '',
        publisher: '',
        isbn: '',
        condition: '',
        rating: '',
        notes: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        imageUrl: ''
      };
    case 'game-wishlist':
      return {
        _id: '',  // Include ID field for matching existing items
        name: '',
        platform: '',
        releaseYear: '',
        genre: '',
        developer: '',
        publisher: '',
        notes: '',
        imageUrl: ''
      };
    case 'console-wishlist':
      return {
        _id: '',  // Include ID field for matching existing items
        name: '',
        manufacturer: '',
        releaseYear: '',
        model: '',
        color: '',
        notes: '',
        imageUrl: ''
      };
    case 'lego-wishlist':
      return {
        _id: '',  // Include ID field for matching existing items
        name: '',
        setNumber: '',
        theme: '',
        releaseYear: '',
        pieces: '',
        minifigures: '',
        notes: '',
        imageUrl: ''
      };
    case 'movie-wishlist':
      return {
        _id: '',  // Include ID field for matching existing items
        name: '',
        director: '',
        releaseYear: '',
        format: '',
        genre: '',
        studio: '',
        notes: '',
        imageUrl: ''
      };
    case 'music-wishlist':
      return {
        _id: '',  // Include ID field for matching existing items
        name: '',
        artist: '',
        releaseYear: '',
        format: '',
        genre: '',
        label: '',
        notes: '',
        imageUrl: ''
      };
    case 'board-games':
      return {
        _id: '',
        name: '',
        designers: '',
        publisher: '',
        year: '',
        minPlayers: '',
        maxPlayers: '',
        playingTime: '',
        minAge: '',
        category: '',
        genre: '',
        language: '',
        condition: '',
        edition: '',
        played: '',
        favourite: '',
        rating: '',
        isSold: '',
        soldDate: '',
        soldPrice: '',
        notes: '',
        imageUrl: ''
      };
    case 'board-game-wishlist':
      return {
        _id: '',
        name: '',
        designers: '',
        publisher: '',
        year: '',
        minPlayers: '',
        maxPlayers: '',
        playingTime: '',
        minAge: '',
        category: '',
        genre: '',
        priority: '',
        rating: '',
        notes: '',
        imageUrl: ''
      };
    default:
      return {};
  }
};

/**
 * Format collection data for CSV export
 * @param {Array} data - Collection data
 * @param {string} collectionType - Type of collection
 * @returns {Array} - Formatted data for CSV export
 */
export const formatDataForCSV = (data, collectionType) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  
  // Create a template row to ensure all fields are included
  const template = getTemplateRow(collectionType);
  
  // Format each item in the collection
  return data.map(item => {
    const formattedItem = { ...template };
    
    // Copy all existing properties from the item
    Object.keys(formattedItem).forEach(key => {
      if (item[key] !== undefined) {
        formattedItem[key] = item[key];
      }
    });
    
    // Ensure _id is included for matching during import
    if (item._id) {
      formattedItem._id = item._id;
    }
    
    return formattedItem;
  });
};
